import React, { useState, useEffect } from 'react';
import { Box, Flex, Heading, Text, Input, Button, Table, Thead, Tbody, Tr, Th, Td, IconButton, useColorModeValue, useDisclosure, FormControl, FormLabel, Select, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, Spinner, useToast, HStack } from '@chakra-ui/react';
import client from '../utils/fetchClient';
import useDebouncedValue from '../utils/useDebouncedValue';
import { FaTrash, FaEdit, FaSyncAlt, FaPlus } from 'react-icons/fa';

function UserModal({ isOpen, onClose, onSave, user, loading }) {
  const [form, setForm] = useState(user || { name: '', email: '', role: '', status: 'Active' });
  useEffect(() => {
    setForm(user || { name: '', email: '', role: '', status: 'Active' });
  }, [user, isOpen]);
  const modalBg = useColorModeValue('white', 'gray.700');
  const inputBg = useColorModeValue('gray.50', 'gray.600');
  const inputBorder = useColorModeValue('gray.200', 'gray.600');
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent bg={modalBg}>
        <ModalHeader>{user ? 'Edit User' : 'New User'}</ModalHeader>
        <ModalBody pb={6}>
          <FormControl mb={4} isRequired>
            <FormLabel>Name</FormLabel>
            <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Enter name" bg={inputBg} borderColor={inputBorder} />
          </FormControl>
          <FormControl mb={4} isRequired>
            <FormLabel>Email</FormLabel>
            <Input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="Enter email" disabled={!!user} bg={inputBg} borderColor={inputBorder} />
          </FormControl>
          <FormControl mb={4} isRequired>
            <FormLabel>Role</FormLabel>
            <Select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} bg={inputBg} borderColor={inputBorder}>
              <option value="">Select role</option>
              <option value="Admin">Admin</option>
              <option value="User">User</option>
            </Select>
          </FormControl>
          <FormControl mb={4} isRequired>
            <FormLabel>Status</FormLabel>
            <Select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} bg={inputBg} borderColor={inputBorder}>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </Select>
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose} mr={3} borderRadius="lg">Cancel</Button>
          <Button colorScheme="green" onClick={() => onSave(form)} borderRadius="lg" isLoading={loading}>Save</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default function SettingsUsersTab() {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { isOpen: isModalOpen, onOpen: onOpenModal, onClose: onCloseModal } = useDisclosure();
  const [editUser, setEditUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const toast = useToast();

  // Chakra color mode values
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const tableHeaderBg = useColorModeValue('green.50', 'gray.700');
  const tableHeaderColor = useColorModeValue('green.700', 'green.200');
  const tableRowBgEven = useColorModeValue('white', 'gray.800');
  const tableRowBgOdd = useColorModeValue('gray.50', 'gray.700');
  const inputBg = useColorModeValue('gray.50', 'gray.700');
  const inputBorder = useColorModeValue('gray.200', 'gray.600');

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await client.get('/api/users');
      if (!res.ok) throw new Error('Failed to fetch users');
      setData(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleAdd = async (user) => {
    setError('');
    setSaving(true);
    try {
      const res = await client.post('/api/users', { body: user });
      if (!res.ok) throw new Error('Failed to add user');
      onCloseModal();
      fetchUsers();
      toast({ title: 'User added.', status: 'success', duration: 3000, isClosable: true });
    } catch (e) {
      setError(e.message);
      toast({ title: 'Failed to add user.', status: 'error', duration: 3000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (user) => {
    setError('');
    setSaving(true);
    try {
      const res = await client.put(`/api/users/${user.id}`, { body: user });
      if (!res.ok) throw new Error('Failed to update user');
      setEditUser(null);
      fetchUsers();
      toast({ title: 'User updated.', status: 'success', duration: 3000, isClosable: true });
    } catch (e) {
      setError(e.message);
      toast({ title: 'Failed to update user.', status: 'error', duration: 3000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setError('');
    try {
      const res = await client.delete(`/api/users/${id}`);
      if (!res.ok) throw new Error('Failed to delete user');
      fetchUsers();
      toast({ title: 'User deleted.', status: 'success', duration: 3000, isClosable: true });
    } catch (e) {
      setError(e.message);
      toast({ title: 'Failed to delete user.', status: 'error', duration: 3000, isClosable: true });
    }
  };

  const debouncedSearch = useDebouncedValue(search, 300);
  const filtered = data
    .filter(row =>
      (row.name || '').toLowerCase().includes(debouncedSearch.toLowerCase()) ||
      (row.email || '').toLowerCase().includes(debouncedSearch.toLowerCase()) ||
      (row.role || '').toLowerCase().includes(debouncedSearch.toLowerCase()) ||
      (row.status || '').toLowerCase().includes(debouncedSearch.toLowerCase())
    )
    .filter(row => (roleFilter ? row.role === roleFilter : true))
    .filter(row => (statusFilter ? row.status === statusFilter : true));

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const startIdx = (page - 1) * pageSize;
  const pageRows = filtered.slice(startIdx, startIdx + pageSize);

  const updateUserField = async (user, field, value) => {
    setUpdatingId(user.id);
    try {
      const updated = { ...user, [field]: value };
      setData(prev => prev.map(u => (u.id === user.id ? updated : u)));
      const res = await client.put(`/api/users/${user.id}`, { body: { [field]: value } });
      if (!res.ok) throw new Error('Failed to update user');
      toast({ title: 'User updated.', status: 'success', duration: 2000, isClosable: true });
    } catch (e) {
      toast({ title: 'Failed to update user.', description: e.message, status: 'error', duration: 3000, isClosable: true });
      // Refetch to revert optimistic change
      fetchUsers();
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <Box>
      <Heading as="h3" size="md" mb={2} color={textColor}>Users</Heading>
      <Text fontSize="sm" color="gray.500" mb={6}>Manage users, roles, and access.</Text>
      <Flex mb={6} justify="space-between" align="center" flexWrap="wrap" gap={4}>
        <Flex gap={2}>
          <Button leftIcon={<FaSyncAlt />} colorScheme="green" onClick={fetchUsers} size="sm" borderRadius="lg">Refresh</Button>
          <Button leftIcon={<FaPlus />} colorScheme="green" onClick={() => { setEditUser(null); onOpenModal(); }} size="sm" borderRadius="lg">New User</Button>
        </Flex>
        <HStack>
          <Input
            placeholder="Search"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            size="sm"
            borderRadius="full"
            maxW="220px"
            minW="120px"
            bg={inputBg}
            borderColor={inputBorder}
          />
          <Select size="sm" value={roleFilter} onChange={e => { setRoleFilter(e.target.value); setPage(1); }}>
            <option value="">All roles</option>
            <option value="Admin">Admin</option>
            <option value="User">User</option>
          </Select>
          <Select size="sm" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
            <option value="">All statuses</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </Select>
          <Select size="sm" value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}>
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
          </Select>
        </HStack>
      </Flex>
      {error && <Text color="red.500" mb={2}>{error}</Text>}
      {loading ? (
        <Flex align="center" justify="center" minH="120px"><Spinner /></Flex>
      ) : filtered.length === 0 ? (
        <Box borderWidth={1} borderRadius="lg" bg={cardBg} borderColor={borderColor} p={8} textAlign="center">
          <Heading size="sm" mb={2}>No users found</Heading>
          <Text color="gray.500" mb={4}>Invite or add a new user to get started.</Text>
          <Button leftIcon={<FaPlus />} colorScheme="green" onClick={() => { setEditUser(null); onOpenModal(); }}>New User</Button>
        </Box>
      ) : (
        <Box overflowX="auto" borderRadius="lg" boxShadow="md" borderWidth={1} borderColor={borderColor} bg={cardBg}>
          <Table variant="simple" size="sm">
            <Thead bg={tableHeaderBg} color={tableHeaderColor}>
              <Tr>
                <Th>Name</Th>
                <Th>Email</Th>
                <Th>Role</Th>
                <Th>Status</Th>
                <Th textAlign="right">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {pageRows.map((row, i) => (
                <Tr key={row.id} bg={i % 2 === 0 ? tableRowBgEven : tableRowBgOdd}>
                  <Td color={textColor}>{row.name}</Td>
                  <Td color={textColor}>{row.email}</Td>
                  <Td color={textColor}>
                    <Select size="sm" value={row.role} isDisabled={updatingId === row.id} onChange={e => updateUserField(row, 'role', e.target.value)}>
                      <option value="Admin">Admin</option>
                      <option value="User">User</option>
                    </Select>
                  </Td>
                  <Td color={textColor}>
                    <Select size="sm" value={row.status} isDisabled={updatingId === row.id} onChange={e => updateUserField(row, 'status', e.target.value)}>
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </Select>
                  </Td>
                  <Td textAlign="right">
                    <IconButton
                      aria-label="Delete user"
                      icon={<FaTrash />}
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => handleDelete(row.id)}
                      mr={2}
                    />
                    <IconButton
                      aria-label="Edit user"
                      icon={<FaEdit />}
                      size="sm"
                      variant="ghost"
                      colorScheme="green"
                      onClick={() => { setEditUser(row); onOpenModal(); }}
                    />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
      {/* Pagination */}
      {filtered.length > 0 && (
        <HStack mt={4} justify="space-between">
          <Text fontSize="sm" color="gray.500">Page {page} of {totalPages}</Text>
          <HStack>
            <Button size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} isDisabled={page === 1}>Previous</Button>
            <Button size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} isDisabled={page === totalPages}>Next</Button>
          </HStack>
        </HStack>
      )}
      <UserModal isOpen={isModalOpen} onClose={onCloseModal} onSave={editUser ? handleEdit : handleAdd} user={editUser} loading={saving} />
    </Box>
  );
} 